import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';

import { AuthApiService, ChangePasswordResponse } from '../../services/auth-api.service';
import { I18nService } from '../../services/i18n.service';
import { AuthCardComponent } from '../../ui/auth-card.component';

const passwordPattern = /^(?=.*[A-Za-zА-Яа-яЁё])(?=.*\d).{8,}$/;

const matchPasswords: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
  const a = group.get('newPassword')?.value;
  const b = group.get('confirmPassword')?.value;
  return a && b && a !== b ? { mismatch: true } : null;
};

type FieldName = 'oldPassword' | 'newPassword' | 'confirmPassword';

/** Authenticated change-password page. Uses session cookie set after /api/auth/login —
 *  401 response means session expired, fall back to login while preserving the
 *  outer continueUrl so the user lands back where they started.
 */
@Component({
  selector: 'auth-change-password',
  standalone: true,
  imports: [ReactiveFormsModule, AuthCardComponent],
  template: `
    <auth-card [title]="i18n.t('change.heading')" [subtitle]="i18n.t('change.subtitle')">
      @if (done()) {
        <p class="au-alert au-alert--ok">{{ i18n.t('change.success') }}</p>
        <button type="button" class="au-button" (click)="goContinue()">
          {{ i18n.t('forgot.back') }}
        </button>
      } @else {
        @if (errorCode()) {
          <p class="au-alert au-alert--error">{{ i18n.t('change.error.' + errorCode()) }}</p>
        }

        <form [formGroup]="form" (ngSubmit)="submit()" novalidate>
          <div class="au-field">
            <label class="au-label" for="oldPassword">{{ i18n.t('change.current_password') }}</label>
            <input id="oldPassword" [type]="show().old ? 'text' : 'password'"
                   autocomplete="current-password" required
                   class="au-input"
                   [class.au-input--error]="invalid('oldPassword')"
                   [formControl]="form.controls.oldPassword">
            <button type="button" class="au-toggle" (click)="toggle('old')"
                    [attr.aria-label]="show().old ? i18n.t('change.hide_password') : i18n.t('change.show_password')">
              @if (show().old) {
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3l18 18"/><path d="M10.58 10.58a2 2 0 002.83 2.83"/><path d="M9.88 4.24A10.94 10.94 0 0112 4c7 0 10 8 10 8a13.16 13.16 0 01-1.67 2.68"/><path d="M6.61 6.61A13.526 13.526 0 002 12s3 8 10 8a10.94 10.94 0 005.76-1.61"/></svg>
              } @else {
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 12s3-8 10-8 10 8 10 8-3 8-10 8-10-8-10-8z"/><circle cx="12" cy="12" r="3"/></svg>
              }
            </button>
          </div>

          <div class="au-field">
            <label class="au-label" for="newPassword">{{ i18n.t('change.new_password') }}</label>
            <input id="newPassword" [type]="show().neu ? 'text' : 'password'"
                   autocomplete="new-password" required
                   class="au-input"
                   [class.au-input--error]="invalid('newPassword')"
                   [formControl]="form.controls.newPassword">
            <button type="button" class="au-toggle" (click)="toggle('neu')"
                    [attr.aria-label]="show().neu ? i18n.t('change.hide_password') : i18n.t('change.show_password')">
              @if (show().neu) {
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3l18 18"/><path d="M10.58 10.58a2 2 0 002.83 2.83"/><path d="M9.88 4.24A10.94 10.94 0 0112 4c7 0 10 8 10 8a13.16 13.16 0 01-1.67 2.68"/><path d="M6.61 6.61A13.526 13.526 0 002 12s3 8 10 8a10.94 10.94 0 005.76-1.61"/></svg>
              } @else {
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 12s3-8 10-8 10 8 10 8-3 8-10 8-10-8-10-8z"/><circle cx="12" cy="12" r="3"/></svg>
              }
            </button>
            @if (invalid('newPassword')) {
              <span class="au-error">{{ i18n.t('change.password_invalid') }}</span>
            } @else {
              <span class="au-hint">{{ i18n.t('change.password_hint') }}</span>
            }
          </div>

          <div class="au-field">
            <label class="au-label" for="confirmPassword">{{ i18n.t('change.confirm_password') }}</label>
            <input id="confirmPassword" [type]="show().confirm ? 'text' : 'password'"
                   autocomplete="new-password" required
                   class="au-input"
                   [class.au-input--error]="form.errors?.['mismatch'] && form.controls.confirmPassword.touched"
                   [formControl]="form.controls.confirmPassword">
            <button type="button" class="au-toggle" (click)="toggle('confirm')"
                    [attr.aria-label]="show().confirm ? i18n.t('change.hide_password') : i18n.t('change.show_password')">
              @if (show().confirm) {
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3l18 18"/><path d="M10.58 10.58a2 2 0 002.83 2.83"/><path d="M9.88 4.24A10.94 10.94 0 0112 4c7 0 10 8 10 8a13.16 13.16 0 01-1.67 2.68"/><path d="M6.61 6.61A13.526 13.526 0 002 12s3 8 10 8a10.94 10.94 0 005.76-1.61"/></svg>
              } @else {
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 12s3-8 10-8 10 8 10 8-3 8-10 8-10-8-10-8z"/><circle cx="12" cy="12" r="3"/></svg>
              }
            </button>
            @if (form.errors?.['mismatch'] && form.controls.confirmPassword.touched) {
              <span class="au-error">{{ i18n.t('change.password_mismatch') }}</span>
            }
          </div>

          <div class="au-actions">
            <button type="submit" class="au-button" [disabled]="loading()">
              {{ loading() ? i18n.t('common.loading') : i18n.t('change.submit') }}
            </button>
            <button type="button" class="au-button au-button--ghost" (click)="cancel()">
              {{ i18n.t('change.cancel') }}
            </button>
          </div>
        </form>
      }

    </auth-card>
  `,
  styles: [`
    .au-field { position: relative; }
    .au-toggle {
      position: absolute;
      right: 8px;
      top: 26px;
      background: none;
      border: none;
      cursor: pointer;
      color: var(--au-text-soft);
      padding: 6px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border-radius: 6px;
      transition: color .15s, background-color .15s;
    }
    .au-toggle:hover { color: var(--au-primary); background: var(--au-bg-soft, rgba(0,0,0,.04)); }
    .au-toggle svg { display: block; }
    .au-hint { font-size: 12px; color: var(--au-text-soft); margin-top: 4px; display: block; }
    .au-actions { display: flex; gap: 12px; align-items: stretch; }
    .au-actions .au-button { flex: 1; }
    .au-button--ghost {
      background: transparent;
      color: var(--au-text-soft);
      border: 1px solid var(--au-border, rgba(0,0,0,.12));
    }
    .au-button--ghost:hover { background: var(--au-bg-soft, rgba(0,0,0,.04)); color: var(--au-text); }
  `],
})
export class ChangePasswordComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private api = inject(AuthApiService);
  protected i18n = inject(I18nService);

  protected langPath: 'ru' | 'kz' = 'ru';
  protected continueUrl = '/';

  readonly form = new FormGroup(
    {
      oldPassword: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
      newPassword: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.pattern(passwordPattern)],
      }),
      confirmPassword: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    },
    { validators: matchPasswords },
  );

  readonly loading = signal(false);
  readonly done = signal(false);
  readonly errorCode = signal<string>('');
  readonly show = signal({ old: false, neu: false, confirm: false });

  invalid(name: FieldName): boolean {
    const c = this.form.controls[name];
    return c.invalid && (c.dirty || c.touched);
  }

  toggle(which: 'old' | 'neu' | 'confirm'): void {
    const s = this.show();
    this.show.set({ ...s, [which]: !s[which] });
  }

  async ngOnInit(): Promise<void> {
    const langParam = this.route.snapshot.paramMap.get('lang');
    this.langPath = langParam === 'kz' ? 'kz' : 'ru';
    await this.i18n.use(I18nService.normaliseFromUrl(langParam));

    const cont = this.route.snapshot.queryParamMap.get('continueUrl');
    if (cont) this.continueUrl = cont;

    // Preflight: если сессия протухла — сразу на login, чтобы пользователь не
    // заполнял форму впустую и не упёрся в 401 на submit.
    this.api.checkSession().subscribe({
      error: (err: HttpErrorResponse) => {
        if (err.status === 401) this.redirectToLogin();
      },
    });
  }

  private redirectToLogin(): void {
    const back = `/${this.langPath}/change-password?continueUrl=${encodeURIComponent(this.continueUrl)}`;
    window.location.href = `/${this.langPath}/login?continueUrl=${encodeURIComponent(back)}`;
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.loading.set(true);
    this.errorCode.set('');
    this.api.changePassword({
      oldPassword: this.form.value.oldPassword!,
      newPassword: this.form.value.newPassword!,
      confirmPassword: this.form.value.confirmPassword!,
    }).subscribe({
      next: (resp: ChangePasswordResponse) => {
        if (resp.ok) {
          this.done.set(true);
          this.loading.set(false);
        } else {
          this.loading.set(false);
          this.errorCode.set(resp.errorCode || 'generic');
        }
      },
      error: (err: HttpErrorResponse) => {
        this.loading.set(false);
        if (err.status === 401) {
          this.redirectToLogin();
          return;
        }
        this.errorCode.set(err.error?.errorCode || 'generic');
      },
    });
  }

  cancel(): void {
    window.location.href = this.continueUrl || '/';
  }

  goContinue(): void {
    window.location.href = this.continueUrl || '/';
  }
}
